/**
 * Task #1960 — Public surface hardening tables.
 *
 * Backs `server/middleware/waf.ts`, `captcha-providers.ts`,
 * `per-tenant-budget.ts`, and `storage/bot-signals.ts`.
 */
import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  integer,
  bigserial,
  boolean,
  timestamp,
  jsonb,
  unique,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

export const botSignals = pgTable(
  "bot_signals",
  {
    id: bigserial("id", { mode: "number" }).primaryKey(),
    surface: text("surface").notNull(),
    ip: text("ip").notNull(),
    signal: text("signal").notNull(),
    tenantId: text("tenant_id"),
    count: integer("count").notNull().default(1),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    observedAt: timestamp("observed_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    idxSurfaceTs: index("idx_bot_signals_surface_ts").on(t.surface, t.observedAt),
    idxIpTs: index("idx_bot_signals_ip_ts").on(t.ip, t.observedAt),
    idxTenantTs: index("idx_bot_signals_tenant_ts").on(t.tenantId, t.observedAt),
  }),
);
export const insertBotSignalSchema = createInsertSchema(botSignals).omit({
  id: true,
  observedAt: true,
});
export type InsertBotSignal = z.infer<typeof insertBotSignalSchema>;
export type BotSignal = typeof botSignals.$inferSelect;

export const publicSurfaceBudgets = pgTable(
  "public_surface_budgets",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    surface: text("surface").notNull(),
    maxPerWindow: integer("max_per_window").notNull(),
    windowMs: integer("window_ms").notNull().default(60_000),
    notes: text("notes"),
    updatedBy: text("updated_by"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uqTenantSurface: unique("uq_public_surface_budgets_tenant_surface").on(
      t.tenantId,
      t.surface,
    ),
    idxSurface: index("idx_public_surface_budgets_surface").on(t.surface),
  }),
);
export const insertPublicSurfaceBudgetSchema = createInsertSchema(
  publicSurfaceBudgets,
).omit({ id: true, updatedAt: true });
export type InsertPublicSurfaceBudget = z.infer<
  typeof insertPublicSurfaceBudgetSchema
>;
export type PublicSurfaceBudget = typeof publicSurfaceBudgets.$inferSelect;

export const captchaProviderConfigs = pgTable(
  "captcha_provider_configs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    surface: text("surface").notNull(),
    provider: text("provider").notNull(),
    required: boolean("required").notNull().default(true),
    notes: text("notes"),
    updatedBy: text("updated_by"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uqTenantSurface: unique("uq_captcha_provider_configs_tenant_surface").on(
      t.tenantId,
      t.surface,
    ),
  }),
);
export const insertCaptchaProviderConfigSchema = createInsertSchema(
  captchaProviderConfigs,
).omit({ id: true, updatedAt: true });
export type InsertCaptchaProviderConfig = z.infer<
  typeof insertCaptchaProviderConfigSchema
>;
export type CaptchaProviderConfig = typeof captchaProviderConfigs.$inferSelect;

export const wafRuleOverrides = pgTable(
  "waf_rule_overrides",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    ruleId: text("rule_id").notNull(),
    action: text("action").notNull(),
    reason: text("reason"),
    updatedBy: text("updated_by"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uqTenantRule: unique("uq_waf_rule_overrides_tenant_rule").on(
      t.tenantId,
      t.ruleId,
    ),
    idxRule: index("idx_waf_rule_overrides_rule").on(t.ruleId),
  }),
);
export const insertWafRuleOverrideSchema = createInsertSchema(
  wafRuleOverrides,
).omit({ id: true, updatedAt: true });
export type InsertWafRuleOverride = z.infer<typeof insertWafRuleOverrideSchema>;
export type WafRuleOverride = typeof wafRuleOverrides.$inferSelect;
